/**
 * useMakeMap — activates map mode and fits the map to the bounding box of
 * all locations tied to a given day (basecamp + scene locations + support locations).
 * Auto-places annotation icons: ⭐ for scene active locations, 🚐 for basecamp,
 * P for parking support locations, 🍽️ for lunch/dinner support locations.
 */
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import mapboxgl from 'mapbox-gl';
import toast from 'react-hot-toast';
import { api } from '../services/api.js';
import { getGlobalMapInstance, useMapStore } from '../stores/mapStore.js';
import { useUiStore } from '../stores/uiStore.js';
import type { Day, DayScene, Pin, LocationSupport, MapAnnotation, AnnotationType } from '../types/index.js';

export function useMakeMap() {
  const qc = useQueryClient();
  const { setMapMode, setAnnotations } = useMapStore();
  const { sidebarOpen, toggleSidebar } = useUiStore();

  return useCallback(async (day: Day) => {
    const map = getGlobalMapInstance();
    if (!map) { toast.error('Map not ready'); return; }

    const points: [number, number][] = [];

    // Dedup helper: type + rounded lat/lng
    const seen = new Set<string>();
    const pendingAnnotations: MapAnnotation[] = [];
    const addAnnotation = (type: AnnotationType, lng: number, lat: number, label?: string) => {
      const key = `${type}_${Math.round(lng * 10000)}_${Math.round(lat * 10000)}`;
      if (!seen.has(key)) {
        seen.add(key);
        pendingAnnotations.push({ id: crypto.randomUUID(), type, lat, lng, label });
      }
    };

    // Include basecamp → trailer annotation
    if (day.basecampLat && day.basecampLng) {
      points.push([day.basecampLng, day.basecampLat]);
      addAnnotation('trailer', day.basecampLng, day.basecampLat);
    }

    // Get scenes for this day — try cache first
    let scenes: DayScene[] = qc.getQueryData<DayScene[]>(['day-scenes', day.id]) ?? [];
    if (!scenes.length) {
      const res = await api.get<DayScene[]>(`/days/${day.id}/scenes`);
      if (res.success) scenes = res.data;
    }

    // Collect all location IDs from pins (to fetch support locations)
    const locationIds = new Set<string>();

    for (const scene of scenes) {
      if (scene.lat && scene.lng) {
        points.push([scene.lng as number, scene.lat as number]);
        addAnnotation('star', scene.lng as number, scene.lat as number);
      }

      // Get pins for this scene — try cache first
      const cached = qc.getQueryData<{ pins: Pin[] }>(['pins', scene.sceneId]);
      const pins: Pin[] = cached?.pins ?? [];

      if (!cached) {
        const res = await api.get<{ pins: Pin[] }>(`/pins?sceneId=${scene.sceneId}&limit=200`);
        if (res.success) {
          for (const pin of res.data.pins) {
            points.push([pin.lng, pin.lat]);
            if (pin.locationId) locationIds.add(pin.locationId);
          }
        }
      } else {
        for (const pin of pins) {
          points.push([pin.lng, pin.lat]);
          if (pin.locationId) locationIds.add(pin.locationId);
        }
      }
    }

    // Fetch support locations and add to bounds + auto-annotate
    for (const locId of locationIds) {
      const cached = qc.getQueryData<LocationSupport[]>(['location-supports', locId]);
      const supports: LocationSupport[] = cached ?? [];

      if (!cached) {
        const res = await api.get<{ supports: LocationSupport[] }>(`/locations/${locId}/supports`);
        if (res.success) supports.push(...res.data.supports);
      }

      for (const sup of supports) {
        points.push([sup.lng, sup.lat]);
        const nameLower = sup.name.toLowerCase();
        if (nameLower.includes('parking')) {
          addAnnotation('parking', sup.lng, sup.lat);
        } else if (nameLower.includes('lunch') || nameLower.includes('dinner') || nameLower.includes('catering')) {
          addAnnotation('lunch', sup.lng, sup.lat);
        }
      }
    }

    if (points.length === 0) {
      toast('No locations found for this day — add scenes or a basecamp first', { icon: 'ℹ️' });
      return;
    }

    // Build bounds and fit
    const bounds = points.reduce(
      (b, pt) => b.extend(pt as [number, number]),
      new mapboxgl.LngLatBounds(points[0], points[0]),
    );
    map.fitBounds(bounds, { padding: 80, maxZoom: 16, duration: 1000 });

    // Place auto-annotations
    setAnnotations(pendingAnnotations);

    // Close sidebar for a clean map view, then activate map mode
    if (sidebarOpen) toggleSidebar();
    setMapMode(true);
  }, [qc, setMapMode, setAnnotations, sidebarOpen, toggleSidebar]);
}
